import { useState, useEffect } from 'react';

// Hook pro práci s rolemi a oprávněními
export const useRolePermissions = (userRole) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Vyčištění starých localStorage dat
  useEffect(() => {
    localStorage.removeItem('crm_roles');
  }, []);

  // Výchozí oprávnění pro role
  const defaultPermissions = {
    admin: {
      viewAllUsers: true,
      viewContacts: true,
      viewChat: true,
      viewDashboard: true,
      viewActivities: true,
      viewCommissions: true,
      viewRequests: true,
      viewAdmin: true,
      viewBackup: true,
      editCompanies: true,
      deleteCompanies: true,
      exportData: true,
      manageUsers: true,
      viewStatusEditor: true,
      viewBatchVisibility: true,
      viewKnowledge: true,
      viewContactManagement: true,
      viewMeetingPlanner: true,
      editUserPermissions: true,
      viewTransferHistory: true,
      manageTeamleader: true,
      viewDiscord: true,
      importContacts: true,
      deleteContacts: true,
      transferContacts: true,
      editForecast: true,
      viewAllCompanies: true,
      editOwnCompanies: true,
      editAllCompanies: true
    },
    obchodnik: {
      viewAllUsers: true,
      viewContacts: true,
      viewChat: true,
      viewDashboard: true,
      viewActivities: true,
      viewCommissions: true,
      viewRequests: true,
      viewAdmin: false,
      viewBackup: false,
      editCompanies: true,
      deleteCompanies: false,
      exportData: false,
      manageUsers: false,
      viewStatusEditor: false,
      viewBatchVisibility: false,
      viewKnowledge: false,
      viewContactManagement: false,
      viewMeetingPlanner: true,
      editUserPermissions: false,
      viewTransferHistory: false,
      manageTeamleader: false,
      viewDiscord: false,
      importContacts: false,
      deleteContacts: false,
      transferContacts: false,
      editForecast: true,
      viewAllCompanies: true,
      editOwnCompanies: true,
      editAllCompanies: false
    },
    podpora: {
      viewAllUsers: true,
      viewContacts: true,
      viewChat: true,
      viewDashboard: true,
      viewActivities: true,
      viewCommissions: false,
      viewRequests: true,
      viewAdmin: false,
      viewBackup: false,
      editCompanies: true,
      deleteCompanies: false,
      exportData: false,
      manageUsers: false,
      viewStatusEditor: false,
      viewBatchVisibility: false,
      viewKnowledge: false,
      viewContactManagement: true,
      viewMeetingPlanner: true,
      editUserPermissions: false,
      viewTransferHistory: true,
      manageTeamleader: false,
      viewDiscord: false,
      importContacts: false,
      deleteContacts: false,
      transferContacts: true,
      editForecast: false,
      viewAllCompanies: true,
      editOwnCompanies: true,
      editAllCompanies: false
    },
    callcentrum: {
      viewAllUsers: true,
      viewContacts: true,
      viewChat: true,
      viewDashboard: false,
      viewActivities: false,
      viewCommissions: false,
      viewRequests: false,
      viewAdmin: false,
      viewBackup: false,
      editCompanies: true,
      deleteCompanies: false,
      exportData: false,
      manageUsers: false,
      viewStatusEditor: false,
      viewBatchVisibility: false,
      viewKnowledge: false,
      viewContactManagement: false,
      viewMeetingPlanner: true,
      editUserPermissions: false,
      viewTransferHistory: false,
      manageTeamleader: false,
      viewDiscord: false,
      importContacts: false,
      deleteContacts: false,
      transferContacts: false,
      editForecast: false,
      viewAllCompanies: false,
      editOwnCompanies: true,
      editAllCompanies: false
    },
    test: {
      viewAllUsers: false, // Klíčové - test role nevidí jiné uživatele
      viewContacts: true,
      viewChat: true, // Může používat AI a skupinový chat
      viewDashboard: true,
      viewActivities: true,
      viewCommissions: false,
      viewRequests: false,
      viewAdmin: true, // Změněno na true pro test správy uživatelů
      viewBackup: false,
      editCompanies: true,
      deleteCompanies: false,
      exportData: false,
      manageUsers: true, // Změněno na true pro test správy uživatelů
      viewStatusEditor: false,
      viewBatchVisibility: false,
      viewKnowledge: false,
      viewContactManagement: false,
      viewMeetingPlanner: true,
      editUserPermissions: false,
      viewTransferHistory: false,
      manageTeamleader: false,
      viewDiscord: false,
      importContacts: false,
      deleteContacts: false,
      transferContacts: false,
      editForecast: true,
      viewAllCompanies: false,
      editOwnCompanies: true,
      editAllCompanies: false
    },
    database: {
      viewAllUsers: false,
      viewContacts: false,
      viewChat: false,
      viewDashboard: false,
      viewActivities: false,
      viewCommissions: false,
      viewRequests: false,
      viewAdmin: false,
      viewBackup: false,
      editCompanies: false,
      deleteCompanies: false,
      exportData: false,
      manageUsers: false,
      viewStatusEditor: false,
      viewBatchVisibility: false,
      viewKnowledge: false,
      viewContactManagement: false,
      viewMeetingPlanner: false,
      editUserPermissions: false,
      viewTransferHistory: false,
      manageTeamleader: false,
      viewDiscord: false,
      importContacts: false,
      deleteContacts: false,
      transferContacts: false,
      editForecast: false,
      viewAllCompanies: false,
      editOwnCompanies: false,
      editAllCompanies: false
    }
  };

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setLoading(true);
        
        // Zkusit načíst z API
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://crm-backend-6jqe.onrender.com'}/api/roles`);
        
        if (response.ok) {
          const rolesData = await response.json();
          const userRoleData = rolesData.find(role => role.name === userRole);
          
          if (userRoleData) {
            setPermissions(userRoleData.permissions);
          } else {
            // Fallback na výchozí oprávnění
            setPermissions(defaultPermissions[userRole] || defaultPermissions.obchodnik);
          }
        } else {
          throw new Error('API nedostupné');
        }
      } catch (error) {
        console.log('API nedostupné, používám výchozí oprávnění');
        setPermissions(defaultPermissions[userRole] || defaultPermissions.obchodnik);
      } finally {
        setLoading(false);
      }
    };

    if (userRole) {
      loadPermissions();
    } else {
      // Pokud není userRole, nastav výchozí oprávnění
      setPermissions(defaultPermissions.obchodnik);
      setLoading(false);
    }
  }, [userRole]);

  const hasPermission = (permission) => {
    // Pokud se stále načítá a je to admin, předpokládej že má oprávnění
    if (loading && userRole === 'admin') {
      return true;
    }
    
    // Pokud permissions nejsou nastaveny, použij defaultní
    if (Object.keys(permissions).length === 0 && defaultPermissions[userRole]) {
      return defaultPermissions[userRole][permission] === true;
    }
    
    return permissions[permission] === true;
  };

  const hasAnyPermission = (permissionsList) => {
    return permissionsList.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissionsList) => {
    return permissionsList.every(permission => hasPermission(permission));
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    loading
  };
};

// Utility funkce pro filtrování uživatelů podle role
export const filterUsersByRole = (users, currentUserRole, permissions) => {
  // Pokud je to test role a nemá oprávnění viewAllUsers, zobraz pouze sebe
  if (!permissions.viewAllUsers) {
    const currentUserId = localStorage.getItem('userId');
    return users.filter(user => user._id === currentUserId);
  }
  
  return users;
};

// Utility funkce pro kontrolu viditelnosti komponenty
export const shouldShowComponent = (componentName, permissions) => {
  const componentPermissions = {
    dashboard: 'viewDashboard',
    activities: 'viewActivities',
    commissions: 'viewCommissions',
    requests: 'viewRequests',
    admin: 'viewAdmin',
    backup: 'viewBackup',
    contacts: 'viewContacts',
    chat: 'viewChat'
  };

  const requiredPermission = componentPermissions[componentName];
  return requiredPermission ? permissions[requiredPermission] : true;
};
